import React, { useState, useEffect } from 'react';
import { isLocked, normalizeStatus } from '../utils/configItemLifecycle';
import { ArrowRight, ArrowLeft, Check, Sparkles, Info, AlertCircle, Lightbulb, ChevronRight, X, Plus, Settings } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { QuestionManagement } from './QuestionManagement';
import { ServiceProjectManager } from './ServiceProjectManager';
import { CategoryLauncherScreen } from './CategoryLauncherScreen';
import { DiscoveryQuestionsPanel } from './DiscoveryQuestionsPanel';
import { projectId } from '../utils/supabase/info';
import type { HierarchicalConfigItem, AIContextCategory } from '../types/phase1e';
import { toast } from 'sonner';
import { selectGuidedQuestions } from '../utils/guidedQuestionSelection';

interface GuidedModeProps {
  companyId: string;
  category: string;
  categoryName: string;
  configItems: HierarchicalConfigItem[];
  accessToken: string;
  currentUser: any;
  onComplete: () => void;
  onExit: () => void;
  /** Open on this question even if it is already answered (edit an existing answer). */
  focusQuestionId?: string;
}

interface Question {
  id: string;
  questionText: string;
  questionType: 'text' | 'number' | 'textarea' | 'boolean' | 'multiple_choice' | 'checkbox' | 'select' | 'list_builder';
  options?: string[];
  placeholder?: string;
  helpText?: string;
  aiContext?: string; // What AI already knows
  skipIf?: string; // Skip this question if condition met
  relatedTo?: string[]; // Related categories/questions
  followUp?: {
    condition: { answer: any };
    question: Question;
  };
}

interface Answer {
  questionId: string;
  answer: any;
  answeredAt: string;
  answeredBy: string;
  confidence: 'high' | 'medium' | 'low';
}

export function GuidedMode({
  companyId,
  category,
  categoryName,
  configItems,
  accessToken,
  currentUser,
  onComplete,
  onExit,
  focusQuestionId,
}: GuidedModeProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [aiContext, setAiContext] = useState<AIContextCategory | null>(null);
  const [currentAnswer, setCurrentAnswer] = useState<any>(null);
  const [customOptions, setCustomOptions] = useState<Record<string, string[]>>({});
  const [newCustomOption, setNewCustomOption] = useState('');
  const [showQuestionManagement, setShowQuestionManagement] = useState(false);
  const [noQuestionsYet, setNoQuestionsYet] = useState(false);

  // Check if user is a consultant (can manage questions)
  const isConsultant = ['consultant', 'admin', 'bdm'].includes(currentUser?.role || currentUser?.userRole || '');

  useEffect(() => {
    loadQuestionsAndContext();
  }, [category, focusQuestionId]);

  // Sync currentAnswer when navigating between questions
  useEffect(() => {
    if (questions[currentQuestionIndex]) {
      const questionId = questions[currentQuestionIndex].id;
      const savedAnswer = answers[questionId];
      setCurrentAnswer(savedAnswer !== undefined ? savedAnswer : null);
    }
  }, [currentQuestionIndex, questions]);

  const loadQuestionsAndContext = async () => {
    setLoading(true);
    try {
      // Special rendering for Category Launcher categories (Incident, Change, Problem)
      const categoryLauncherCategories = ['incidents', 'incident', 'changes', 'change', 'problems', 'problem'];
      if (categoryLauncherCategories.includes(category.toLowerCase())) {
        console.log('[GuidedMode] Category Launcher detected for category:', category);
        console.log('[GuidedMode] Available config items:', configItems.map(item => ({
          id: item.id,
          name: item.name,
          category: item.category,
          status: item.status
        })));

        setLoading(false);
        return; // CategoryLauncherScreen will be rendered below
      }

      // Special handling for Service Request Management - use ServiceProjectManager
      if (category === 'service_requests' || category === 'service-requests') {
        setLoading(false);
        return; // ServiceProjectManager will be rendered below
      }

      // Load AI context for this category
      const contextResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/ai-context/category/${category}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      let context: AIContextCategory | null = null;
      if (contextResponse.ok) {
        context = await contextResponse.json();
        setAiContext(context);
        
        // Pre-fill answers from context
        if (context && context.answers) {
          const existingAnswers: Record<string, any> = {};
          Object.keys(context.answers).forEach(qId => {
            existingAnswers[qId] = context.answers[qId].answer;
          });
          setAnswers(existingAnswers);
        }
      }

      // Load questions for this category (AI-generated based on context)
      const questionsResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/guided-questions/${category}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!questionsResponse.ok) {
        throw new Error('Failed to load questions');
      }

      const generatedQuestions: Question[] = await questionsResponse.json();
      if (!Array.isArray(generatedQuestions) || generatedQuestions.length === 0) {
        // Nothing to ask yet: say so instead of pretending everything was answered.
        console.log('[GUIDED_MODE] No questions exist for category', category);
        setNoQuestionsYet(true);
        setQuestions([]);
        return;
      }

      // Guided Mode shows what is still open; the question being edited (focusQuestionId)
      // stays in and the session starts on it (utils/guidedQuestionSelection.ts).
      const selection = selectGuidedQuestions(generatedQuestions, context?.answers ?? null, { focusQuestionId });
      const unansweredQuestions = selection.questions;
      console.log('[GUIDED_MODE] Total questions:', generatedQuestions.length, '· answered:', selection.answeredCount, '· to show:', unansweredQuestions.length);

      // AI question skipping (REQUIREMENTS Phase 1C): drop what the documents
      // already answer and pre-fill suggestions. Rules-only when AI is off.
      let remainingQuestions = unansweredQuestions;
      const prefills: Record<string, any> = {};
      try {
        const planRes = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/discovery/${companyId}/category/${category}/skip-plan`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (planRes.ok) {
          const plan = await planRes.json();
          const decisions: any[] = Array.isArray(plan?.decisions) ? plan.decisions : [];
          const autoSkips = decisions.filter((d) => d.decidedBy === 'ai' && d.action === 'skip' && d.answer !== undefined);
          const suggestions = decisions.filter((d) => d.action === 'suggest' && d.answer !== undefined);

          if (autoSkips.length > 0) {
            await fetch(
              `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/discovery/${companyId}/category/${category}/skip-plan/apply`,
              {
                method: 'POST',
                headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  accepted: autoSkips.map((d) => ({ questionId: d.questionId, answer: d.answer, confidence: d.confidence, evidence: d.evidence, action: 'skip' })),
                }),
              },
            ).catch(() => {});
          }

          const skippedIds = new Set<string>(decisions.filter((d) => d.action === 'skip').map((d) => d.questionId));
          remainingQuestions = unansweredQuestions.filter((q) => q.id === focusQuestionId || !skippedIds.has(q.id));

          if (suggestions.length > 0) {
            suggestions.forEach((d) => { prefills[d.questionId] = d.answer; });
            setAnswers((prev) => ({ ...prev, ...prefills }));
          }

          const fromDocs = autoSkips.length;
          if (fromDocs > 0 || suggestions.length > 0) {
            toast.success(
              [
                fromDocs > 0 ? `${fromDocs} ${fromDocs === 1 ? 'question' : 'questions'} answered from your documents` : null,
                suggestions.length > 0 ? `${suggestions.length} pre-filled for you to confirm` : null,
              ].filter(Boolean).join(' · '),
            );
          }
          console.log('[GUIDED_MODE] Skip plan:', plan?.aiStatus, plan?.stats);
        }
      } catch (planError) {
        console.warn('[GUIDED_MODE] Skip plan unavailable; showing all questions', planError);
      }

      // If all questions are answered, show completion message
      if (remainingQuestions.length === 0) {
        console.log('[GUIDED_MODE] All questions answered!');
        toast.success('All questions in this category have been answered');
        onComplete();
        return;
      }

      setQuestions(remainingQuestions);

      // Start on the question being edited, else the first open one (pre-filled suggestions
      // and existing answers show as the current answer).
      const startIndex = focusQuestionId ? Math.max(0, remainingQuestions.findIndex((q) => q.id === focusQuestionId)) : 0;
      const first = remainingQuestions[startIndex];
      setCurrentQuestionIndex(startIndex);
      setCurrentAnswer(prefills[first?.id] ?? context?.answers?.[first?.id]?.answer ?? null);

    } catch (error) {
      console.error('[GUIDED_MODE] Error loading:', error);
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = async () => {
    if (!currentQuestion) return;

    setSaving(true);
    try {
      // Save answer
      const newAnswers = { ...answers, [currentQuestion.id]: currentAnswer };
      setAnswers(newAnswers);

      // Save to backend
      await saveAnswer(currentQuestion.id, currentQuestion.questionText, currentAnswer);

      // Check if current question has a follow-up that should be shown
      if (currentQuestion.followUp) {
        const { condition, question: followUpQuestion } = currentQuestion.followUp;
        
        // Check if condition is met
        let showFollowUp = false;
        if (condition.answer !== undefined) {
          showFollowUp = currentAnswer === condition.answer;
        }

        if (showFollowUp) {
          // Insert follow-up question after current one
          const updatedQuestions = [...questions];
          updatedQuestions.splice(currentQuestionIndex + 1, 0, followUpQuestion);
          setQuestions(updatedQuestions);
        }
      }

      // Move to next question or complete
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setCurrentAnswer(answers[questions[currentQuestionIndex + 1]?.id] || null);
      } else {
        // All questions answered - trigger AI analysis
        await triggerAIAnalysis();
        toast.success('Category discovery complete');
        onComplete();
      }

    } catch (error) {
      console.error('[GUIDED_MODE] Error saving answer:', error);
      toast.error('Failed to save answer');
    } finally {
      setSaving(false);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setCurrentAnswer(answers[questions[currentQuestionIndex - 1]?.id] || null);
    }
  };

  const determineConfidence = (answer: any): 'high' | 'medium' | 'low' => {
    // For structured answers (boolean, multiple choice), auto-set high confidence
    if (typeof answer === 'boolean') return 'high';
    if (Array.isArray(answer)) return answer.length > 0 ? 'high' : 'low';
    
    // For text answers, set to 'medium' initially - AI will review later
    // This ensures text answers are reviewed by AI for completeness
    if (typeof answer === 'string') {
      if (answer.trim().length === 0) return 'low';
      return 'medium'; // AI will analyze and potentially upgrade to 'high'
    }
    
    return 'medium';
  };

  const triggerAIAnalysis = async () => {
    try {
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/ai-context/category/${category}/analyze`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
    } catch (error) {
      console.error('[GUIDED_MODE] Error triggering AI analysis:', error);
    }
  };

  const handleSkipQuestion = async () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer(answers[questions[currentQuestionIndex + 1]?.id] || null);
    } else {
      onComplete();
    }
  };

  const saveAnswer = async (questionId: string, questionText: string, answer: any) => {
    try {
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyId}/ai-context/category/${category}/answer`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            questionId: questionId,
            question: questionText,
            answer: answer,
            answeredBy: currentUser.id,
            answeredByName: currentUser.name || currentUser.email,
            confidence: determineConfidence(answer),
          }),
        }
      );
    } catch (error) {
      console.error('[GUIDED_MODE] Error saving answer:', error);
      toast.error('Failed to save answer');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading questions...</p>
        </div>
      </div>
    );
  }

  // Special rendering for Category Launcher categories (Incident, Change, Problem)
  const categoryLauncherCategories = ['incidents', 'incident', 'changes', 'change', 'problems', 'problem'];
  if (categoryLauncherCategories.includes(category.toLowerCase())) {
    // Find the actual config item for this category
    const categoryKeyword = category.toLowerCase().replace(/s$/, ''); // Remove trailing 's'
    
    const categoryConfigItem = configItems.find(item => {
      const itemName = item.name?.toLowerCase() || '';
      const itemCategory = item.category?.toLowerCase() || '';
      
      return itemName.includes(categoryKeyword) || itemCategory.includes(categoryKeyword);
    });

    console.log('[GuidedMode] Looking for config item:', {
      category,
      categoryKeyword,
      foundItem: categoryConfigItem ? {
        id: categoryConfigItem.id,
        name: categoryConfigItem.name,
        category: categoryConfigItem.category
      } : null
    });

    if (!categoryConfigItem || !categoryConfigItem.id) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
          <Card className="max-w-md bg-white">
            <CardHeader>
              <CardTitle>Configuration Error</CardTitle>
              <CardDescription>No config item found for {categoryName}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={onExit} className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Map category to CategoryLauncherScreen format
    const getCategoryType = (): 'incident_management' | 'change_management' | 'problem_management' | 'service_request_management' => {
      const cat = category.toLowerCase();
      if (cat.includes('incident')) return 'incident_management';
      if (cat.includes('change')) return 'change_management';
      if (cat.includes('problem')) return 'problem_management';
      return 'incident_management'; // fallback
    };

    // Get consulting firm ID from config item
    const consultingFirmId = categoryConfigItem.consultingFirmId || currentUser?.consultingFirmId || '';

    return (
      <CategoryLauncherScreen
        category={getCategoryType()}
        categoryDisplayName={categoryName}
        configItemId={categoryConfigItem.id}
        companyId={companyId}
        sowId={categoryConfigItem.sowId}
        consultingFirmId={consultingFirmId}
        userRole={isConsultant ? 'consultant' : 'client'}
        onBack={onExit}
        categoryStatus={normalizeStatus(categoryConfigItem) === 'approved' ? 'done' : isLocked(normalizeStatus(categoryConfigItem)) ? 'ready-for-implementation' : 'in-progress'}
      />
    );
  }

  // Special rendering for Service Request Management
  if (category === 'service_requests' || category === 'service-requests') {
    // Find actual SRM-related config items from the loaded items
    const srmConfigItems = configItems.filter(item => 
      item.category === 'service_requests' || 
      item.category === 'service-requests' ||
      item.name?.toLowerCase().includes('service request') ||
      item.name?.toLowerCase().includes('request type')
    );

    console.log('[GuidedMode] SRM config items found:', srmConfigItems);

    // If we have specific SRM config items, show them with questions
    if (srmConfigItems.length > 0) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 p-8">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Badge className="mb-2 bg-purple-100 text-purple-700">{categoryName}</Badge>
                  <h1 className="text-2xl font-semibold text-gray-900">Service Request Management</h1>
                  <p className="text-muted-foreground mt-2">
                    Configure your service request types and their workflows.
                  </p>
                </div>
                <Button variant="ghost" onClick={onExit}>
                  <X className="w-4 h-4 mr-2" />
                  Exit
                </Button>
              </div>
            </div>

            {/* Show each SRM config item with its questions */}
            <div className="space-y-6">
              {srmConfigItems.map((item) => (
                <Card key={item.id}>
                  <CardHeader>
                    <CardTitle>{item.name}</CardTitle>
                    {item.description && (
                      <CardDescription>{item.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <DiscoveryQuestionsPanel
                      configItemId={item.id}
                      configItemName={item.name}
                      companyId={companyId}
                      sowId=""
                      accessToken={accessToken}
                      currentUser={currentUser}
                      mode="config-item"
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // No SRM config items exist yet - show the ServiceProjectManager to create them
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <Badge className="mb-2 bg-purple-100 text-purple-700">{categoryName}</Badge>
                <h1 className="text-2xl font-semibold text-gray-900">Service Request Management</h1>
                <p className="text-muted-foreground mt-2">
                  Define different types of service requests (Hardware, Software, Access, etc.) 
                  with custom fields, approvals, and workflows for each type.
                </p>
              </div>
              <Button variant="ghost" onClick={onExit}>
                <X className="w-4 h-4 mr-2" />
                Exit
              </Button>
            </div>
          </div>

          {/* ServiceProjectManager to create request types */}
          <ServiceProjectManager
            configItemId={`srm-manager-${companyId}`}
            companyId={companyId}
            accessToken={accessToken}
            currentUser={currentUser}
            onComplete={() => {
              // Reload and show questions
              window.location.reload();
            }}
          />
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 px-4">
        <Card className="w-full max-w-md bg-white">
          <CardHeader>
            <CardTitle>{noQuestionsYet ? 'Nothing to answer here yet' : 'No Questions Needed'}</CardTitle>
            <CardDescription>
              {noQuestionsYet
                ? `Your consultant has not added questions for ${categoryName || 'this area'} yet. Nothing is needed from you here.`
                : 'AI has gathered all necessary information for this category'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={noQuestionsYet ? onExit : onComplete} className="w-full bg-purple-600 hover:bg-purple-700">
              {noQuestionsYet ? 'Back' : 'Continue'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = Math.round(((currentQuestionIndex + 1) / questions.length) * 100);

  // Check if current question has been answered (either in currentAnswer state or saved in answers)
  const isAnswered = (currentAnswer !== null && currentAnswer !== undefined) || 
                     (answers[currentQuestion?.id] !== null && answers[currentQuestion?.id] !== undefined);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Badge className="mb-2">{categoryName}</Badge>
              <h1 className="text-2xl font-semibold text-gray-900">Guided Discovery</h1>
            </div>
            <div className="flex gap-2">
              {isConsultant && (
                <Button 
                  variant="outline" 
                  onClick={() => setShowQuestionManagement(true)}
                  className="border-purple-200 text-purple-700 hover:bg-purple-50"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Manage Questions
                </Button>
              )}
              <Button variant="ghost" onClick={onExit}>
                <X className="w-4 h-4 mr-2" />
                Exit
              </Button>
            </div>
          </div>

          {/* Progress */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">
              Question {currentQuestionIndex + 1} of {questions.length}
            </span>
            <span className="text-sm text-gray-600">{progress}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-start gap-2 mb-2">
                  <CardTitle className="text-xl flex-1">{currentQuestion.questionText}</CardTitle>
                  {currentQuestion.helpText && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          className="mt-1 text-gray-400 hover:text-purple-600 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 rounded-full p-1"
                          aria-label="More information"
                        >
                          <Info className="w-5 h-5" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80" align="start">
                        <div className="space-y-2">
                          <div className="flex items-start gap-2">
                            <Info className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-gray-700">
                              {currentQuestion.helpText}
                            </div>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 ml-4">
                <span className="text-purple-700">{currentQuestionIndex + 1}</span>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* AI Context Alert */}
            {currentQuestion.aiContext && (
              <Alert className="border-blue-200 bg-blue-50">
                <Sparkles className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-900">AI Context</AlertTitle>
                <AlertDescription className="text-blue-700">
                  {currentQuestion.aiContext}
                </AlertDescription>
              </Alert>
            )}

            {/* Answer Input */}
            {currentQuestion.questionType === 'text' && (
              <div className="space-y-2">
                <Label>Your Answer</Label>
                <Input
                  value={currentAnswer || ''}
                  onChange={(e) => setCurrentAnswer(e.target.value)}
                  placeholder={currentQuestion.placeholder}
                  className="text-lg"
                />
              </div>
            )}

            {currentQuestion.questionType === 'number' && (
              <div className="space-y-2">
                <Label>Your Answer</Label>
                <Input
                  type="number"
                  value={currentAnswer || ''}
                  onChange={(e) => setCurrentAnswer(e.target.value ? parseInt(e.target.value, 10) : '')}
                  placeholder={currentQuestion.placeholder}
                  className="text-lg"
                />
              </div>
            )}

            {currentQuestion.questionType === 'list_builder' && (
              <div className="space-y-3">
                <Label>Your Answer</Label>
                
                {/* Existing items */}
                {(Array.isArray(currentAnswer) ? currentAnswer : []).map((item, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-purple-700">{index + 1}</span>
                    </div>
                    <Input
                      value={item}
                      onChange={(e) => {
                        const updated = [...(Array.isArray(currentAnswer) ? currentAnswer : [])];
                        updated[index] = e.target.value;
                        setCurrentAnswer(updated);
                      }}
                      placeholder={currentQuestion.placeholder || `Item ${index + 1}`}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const updated = (Array.isArray(currentAnswer) ? currentAnswer : []).filter((_, i) => i !== index);
                        setCurrentAnswer(updated.length > 0 ? updated : null);
                      }}
                      className="flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                
                {/* Add another button */}
                <Button
                  variant="outline"
                  onClick={() => {
                    const current = Array.isArray(currentAnswer) ? currentAnswer : [];
                    setCurrentAnswer([...current, '']);
                  }}
                  className="w-full border-dashed border-2 hover:bg-purple-50 hover:border-purple-300"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Another {currentQuestion.placeholder || 'Item'}
                </Button>
                
                {/* Summary */}
                {Array.isArray(currentAnswer) && currentAnswer.filter(item => item.trim()).length > 0 && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium mb-3 text-blue-900">
                      Added ({currentAnswer.filter(item => item.trim()).length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {currentAnswer.filter(item => item.trim()).map((item, i) => (
                        <Badge key={i} variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200">
                          {item}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Help text */}
                {currentQuestion.helpText && (
                  <p className="text-sm text-muted-foreground">
                    {currentQuestion.helpText}
                  </p>
                )}
              </div>
            )}

            {currentQuestion.questionType === 'textarea' && (
              <div className="space-y-2">
                <Label>Your Answer</Label>
                <Textarea
                  rows={6}
                  value={currentAnswer || ''}
                  onChange={(e) => setCurrentAnswer(e.target.value)}
                  placeholder={currentQuestion.placeholder}
                  className="text-base"
                />
              </div>
            )}

            {currentQuestion.questionType === 'boolean' && (
              <RadioGroup
                value={currentAnswer?.toString()}
                onValueChange={(value) => setCurrentAnswer(value === 'true')}
              >
                <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <RadioGroupItem value="true" id="yes" />
                  <Label htmlFor="yes" className="flex-1 cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <RadioGroupItem value="false" id="no" />
                  <Label htmlFor="no" className="flex-1 cursor-pointer">No</Label>
                </div>
              </RadioGroup>
            )}

            {currentQuestion.questionType === 'multiple_choice' && currentQuestion.options && (
              <RadioGroup
                value={currentAnswer}
                onValueChange={setCurrentAnswer}
              >
                {currentQuestion.options.map((option, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <RadioGroupItem value={option} id={`option-${index}`} />
                    <Label htmlFor={`option-${index}`} className="flex-1 cursor-pointer">
                      {option}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            )}

            {currentQuestion.questionType === 'checkbox' && currentQuestion.options && (() => {
              const selected = Array.isArray(currentAnswer) ? currentAnswer : [];
              const questionCustomOptions = customOptions[currentQuestion.id] || [];
              const allOptions = [...currentQuestion.options, ...questionCustomOptions];
              const allSelected = selected.length === allOptions.length && allOptions.length > 0;
              
              return (
                <div className="space-y-3">
                  <div className="flex items-center justify-start mb-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (allSelected) {
                          setCurrentAnswer([]);
                        } else {
                          setCurrentAnswer(allOptions);
                        }
                      }}
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 -ml-2"
                    >
                      {allSelected ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>

                  {/* Predefined Options */}
                  {currentQuestion.options.map((option, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => {
                        if (selected.includes(option)) {
                          setCurrentAnswer(selected.filter((o: string) => o !== option));
                        } else {
                          setCurrentAnswer([...selected, option]);
                        }
                      }}
                    >
                      <Checkbox
                        id={`checkbox-${index}`}
                        checked={selected.includes(option)}
                        onCheckedChange={() => {}}
                      />
                      <Label htmlFor={`checkbox-${index}`} className="flex-1 cursor-pointer">
                        {option}
                      </Label>
                    </div>
                  ))}

                  {/* Custom Options */}
                  {questionCustomOptions.map((option, index) => (
                    <div
                      key={`custom-${index}`}
                      className="flex items-center space-x-3 p-3 border border-purple-200 bg-purple-50 rounded-lg hover:bg-purple-100 cursor-pointer transition-colors"
                      onClick={() => {
                        if (selected.includes(option)) {
                          setCurrentAnswer(selected.filter((o: string) => o !== option));
                        } else {
                          setCurrentAnswer([...selected, option]);
                        }
                      }}
                    >
                      <Checkbox
                        id={`custom-checkbox-${index}`}
                        checked={selected.includes(option)}
                        onCheckedChange={() => {}}
                      />
                      <Label htmlFor={`custom-checkbox-${index}`} className="flex-1 cursor-pointer">
                        {option}
                      </Label>
                      <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
                        Custom
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          const updated = questionCustomOptions.filter((o) => o !== option);
                          setCustomOptions({
                            ...customOptions,
                            [currentQuestion.id]: updated,
                          });
                          setCurrentAnswer(selected.filter((o: string) => o !== option));
                          toast.success('Custom option removed');
                        }}
                        className="size-6 p-0"
                      >
                        <X className="size-4" />
                      </Button>
                    </div>
                  ))}

                  {/* Add Custom Option */}
                  <div className="mt-4 p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                    <p className="text-sm font-medium mb-2">Add Custom Option</p>
                    <p className="text-xs text-muted-foreground mb-3">
                      Need to track something else? Add your own custom option here.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={newCustomOption}
                        onChange={(e) => setNewCustomOption(e.target.value)}
                        placeholder="e.g., Printers, Tablets, Accessories"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (!newCustomOption.trim()) {
                              toast.error('Please enter an option');
                              return;
                            }
                            if (allOptions.includes(newCustomOption.trim())) {
                              toast.error('This option already exists');
                              return;
                            }
                            const customOpt = newCustomOption.trim();
                            setCustomOptions({
                              ...customOptions,
                              [currentQuestion.id]: [...questionCustomOptions, customOpt],
                            });
                            setCurrentAnswer([...selected, customOpt]);
                            setNewCustomOption('');
                            toast.success('Custom option added');
                          }
                        }}
                      />
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (!newCustomOption.trim()) {
                            toast.error('Please enter an option');
                            return;
                          }
                          if (allOptions.includes(newCustomOption.trim())) {
                            toast.error('This option already exists');
                            return;
                          }
                          const customOpt = newCustomOption.trim();
                          setCustomOptions({
                            ...customOptions,
                            [currentQuestion.id]: [...questionCustomOptions, customOpt],
                          });
                          setCurrentAnswer([...selected, customOpt]);
                          setNewCustomOption('');
                          toast.success('Custom option added');
                        }}
                        className="shrink-0"
                      >
                        <Plus className="size-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  </div>

                  {/* Selected Items Summary */}
                  {selected.length > 0 && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-medium mb-2">
                        Selected ({selected.length})
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {selected.map((item) => (
                          <Badge
                            key={item}
                            variant="secondary"
                            className="bg-blue-100 text-blue-700"
                          >
                            {item}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {currentQuestion.questionType === 'select' && currentQuestion.options && (
              <div className="space-y-2">
                <Label>Select an option</Label>
                <Select
                  value={currentAnswer}
                  onValueChange={setCurrentAnswer}
                >
                  <SelectTrigger className="text-lg">
                    <SelectValue placeholder="Choose one..." />
                  </SelectTrigger>
                  <SelectContent>
                    {currentQuestion.options.map((option, index) => (
                      <SelectItem key={index} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Related Information */}
            {currentQuestion.relatedTo && currentQuestion.relatedTo.length > 0 && (
              <Alert>
                <Lightbulb className="h-4 w-4" />
                <AlertDescription>
                  This answer may relate to: {currentQuestion.relatedTo.join(', ')}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0 || saving}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={handleSkipQuestion}
              disabled={saving}
            >
              Skip
            </Button>
            <Button
              onClick={handleAnswer}
              disabled={saving || (!currentAnswer && currentAnswer !== false)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? 'Saving...' : currentQuestionIndex === questions.length - 1 ? 'Complete' : 'Next Question'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Help Footer */}
        <Card className="mt-6 bg-gray-50 border-gray-200">
          <CardContent className="p-4">
            <div className="flex gap-3 items-start">
              <Info className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="mb-1">
                  <strong>Why are we asking this?</strong>
                </p>
                <p>
                  Your answers help us configure your service desk to match your specific processes and requirements. 
                  The more detail you provide, the better we can tailor the setup to your needs.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Question Management Dialog (Consultants Only) */}
      {isConsultant && showQuestionManagement && (
        <Dialog open={showQuestionManagement} onOpenChange={setShowQuestionManagement}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden p-0">
            <QuestionManagement
              companyId={companyId}
              firmId={currentUser?.firmId || currentUser?.firm_id || ''}
              category={category}
              categoryName={categoryName}
              accessToken={accessToken}
              onClose={() => {
                setShowQuestionManagement(false);
                loadQuestionsAndContext(); // Reload questions after management
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}